/* eslint-disable no-prototype-builtins */
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import { z } from "zod";
import { EditUser, userById } from "~/services/supervisor.services";
import { SubmitButton as Button } from "~/components/form/submit-button";
import { getUserMaster } from "~/services/supervisor.services";
import { toast } from "react-toastify";
import { cn } from "~/utils/tailwind";
const FormSchema = z.object({
  data: z.object({
    user_details: z.array(
      z.object({
        active: z.boolean().default(true),
        firstname: z
          .string()
          .min(1, "First Name is required")
          .regex(/^[a-zA-Z\W_]+$/, "First Name contain only letters and special characters."),
        lastname: z
          .string()
          .min(1, "Last Name is required")
          .regex(/^[a-zA-Z\W_]+$/, "Last Name contain only letters and special characters."),
        domain: z.number().default(1),
        employeeid: z.string().optional(),
        contactnumber: z
          .string()
          .min(10, { message: "Must be a valid mobile number" })
          .max(14, { message: "Must be a valid mobile number" }),
        emailaddress: z.string().email().min(1, "Email ID is required"),
        leadpersonuserid: z.number(),
        iswnsemployee: z.boolean().default(true),
        islocked: z.boolean().default(false),
        isloggedin: z.boolean().default(false),
      }).refine(data => {
        if (data.iswnsemployee && (!data.employeeid || !/^u\d+$/.test(data.employeeid))) {
          return false;
        }
        return true;
      }, {
        message: "Employee ID is required for WNS Employees and must start with ‘u’ followed by digits",
        path: ['employeeid'], // This ensures the error is on employeeid
      })
    ),
    user_doctype: z
      .array(z.record(z.string(), z.union([z.string(), z.number()])))
      .min(1),
    user_roles: z
      .array(z.record(z.string(), z.union([z.string(), z.number()])))
      .min(1),
    user_groups: z
      .array(z.record(z.string(), z.union([z.string(), z.number()])))
      .min(1),
  }),
});

type FormValues = z.infer<typeof FormSchema>;
interface OptionType {
  label: string;
  value: string | number;
}

interface LeadType {
  id?: string;
  rolename?: string;
  username?: string;
  DocType?: string;
  DocId: string;
  GroupName?: string;
  GroupId?: string;
}

const EditModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onEdit: () => void;
}> = ({ isOpen, onClose, userId, onEdit }) => {
  const [roles, setRoles] = useState<OptionType[]>([]);
  const [groups, setGroups] = useState<OptionType[]>([]);
  const [doc_Type, setDoctype] = useState<OptionType[]>([]);
  const [leads, setLeads] = useState<OptionType[]>([]);
  const [isEmployee, setIsEmployee] = useState(true);
  const [userData, setUserData] = useState<FormValues>({
    data: {
      user_details: [
        {
          firstname: "",
          lastname: "",
          employeeid: "",
          contactnumber: "",
          emailaddress: "",
          leadpersonuserid: 0,
          iswnsemployee: true,
          active: true,
          domain: 1,
          isloggedin: false,
          islocked: false,
        },
      ],
      user_roles: [],
      user_doctype: [],
      user_groups: [],
    },
  });

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserData();
    }
  }, [isOpen, userId]);

  // Inside fetchUserData
  const fetchUserData = async () => {
    if (userId) {
      try {
        const { data, status } = await userById(userId);

        if (status) {
          // Map the fetched data to match the form structure
          const mappedData = {
            data: {
              user_details: [
                {
                  firstname:
                    (data as any).data.user_details[0]?.firstname || "",
                  lastname: (data as any).data.user_details[0]?.lastname || "",
                  employeeid:
                    (data as any).data.user_details[0]?.employeeid || "",
                  contactnumber:
                    (data as any).data.user_details[0]?.contactnumber || "",
                  emailaddress:
                    (data as any).data.user_details[0]?.emailaddress || "",
                  leadpersonuserid:
                    (data as any).data.user_details[0]?.leadpersonuserid || 0,
                  iswnsemployee:
                    (data as any).data.user_details[0]?.iswnsemployee ?? true,
                  active: (data as any).data.user_details[0]?.active ?? true,
                  domain: (data as any).data.user_details[0]?.domain || 1,
                  isloggedin:
                    (data as any).data.user_details[0]?.isloggedin || false,
                  islocked:
                    (data as any).data.user_details[0]?.islocked || false,
                },
              ],
              user_roles:
                (data as any).data.user_roles.map((role: any) => ({
                  roleid: role.roleid,
                })) || [],
              user_doctype:
                (data as any).data.user_doctype.map((doctype: any) => ({
                  doctypeid: doctype.doctypeid,
                })) || [],
              user_groups:
                (data as any).data.user_groups.map((group: any) => ({
                  groupid: group.groupid,
                })) || [],
            },
          };
          console.log(mappedData.data.user_details[0].leadpersonuserid);
          // Reset the form with the mapped data
          editUserForm.reset(mappedData);
          setUserData(mappedData); // Optionally update the local state
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    }
  };

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserData();
    }
  }, [isOpen, userId]);

  const allFields = [
    {
      name: "iswnsemployee",
      type: "radio",
      label: "Employee?",
      options: [
        { value: "true", label: "WNS" },
        { value: "false", label: "External" },
      ],
    },
    {
      label: "Employee ID",
      type: "text",
      name: "employeeid",
    },
    {
      name: "firstname",
      type: "text",
      label: "First Name",
    },
    {
      name: "lastname",
      type: "text",
      label: "Last Name",
    },
    {
      label: "Phone Number",
      type: "text",
      name: "contactnumber",
    },
    {
      label: "Email ID",
      type: "text",
      name: "emailaddress",
    },
    {
      label: "Lead Name",
      type: "select",
      name: "leadpersonuserid",
      options: leads,
    },
    {
      name: "active",
      label: "Is Active",
      type: "radio",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      disabled: false
    },
    {
      name: "isloggedin",
      label: "Is Online",
      type: "radio",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      // disabled: true
    }
  ];

  type TextStrings =
    | "firstname"
    | "lastname"
    | "employeeid"
    | "contactnumber"
    | "emailaddress";
  type RadioStrings = "iswnsemployee";
  type SelectStrings = "leadpersonuserid";
  type TextFields = `data.user_details.${number}.${TextStrings}`;
  type SelectFields = `data.user_details.${number}.${SelectStrings}`;
  type RadioFields = `data.user_details.${number}.${RadioStrings}`;

  const defaultValues = userData;

  const editUserForm = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(FormSchema),
    defaultValues,
  });

  const { handleSubmit, control } = editUserForm;

  const { fields } = useFieldArray({ control, name: "data.user_details" });
  const isWnsEmployee = useWatch({
    control,
    name: "data.user_details.0.iswnsemployee",
  });

  useEffect(() => {
    setIsEmployee(isWnsEmployee);
  }, [isWnsEmployee]);

  useEffect(() => {
    if (isEmployee) {
      editUserForm.register("data.user_details.0.employeeid", {
        required: "Employee ID is required",
      });
    } else {
      editUserForm.unregister("data.user_details.0.employeeid");
    }
  }, [isEmployee, editUserForm]);

  const onSubmit = async (data: FormValues) => {
    console.log(data);
    const { data: response } = await EditUser(data, userId);
    console.log((response as any).data);
    if (
      (response as any).data == "success" ||
      (response as any).data == "Success"
    ) {
      toast.success("User Details Updated Successfully", {
        theme: "colored",
      });

      onClose();
      console.log(userData);
      await fetchUserData();
      onEdit()
    } else {
      console.log((response as any).data);
      toast.error((response as any).data, {
        theme: "colored",
      });
    }
  };

  const selectAllRender = (
    field: {
      onChange: (value: any) => void;
      value: Array<Record<string, any>>;
    },
    options: OptionType[],
    key: string
  ) => {
    {
      const selectAllOption = {
        label: "Select All",
        value: "selectAll",
      };

      const optionsWithSelectAll = [selectAllOption, ...options];

      const handleOnChange = (newValue: any) => {
        if (
          newValue &&
          newValue.some((option: OptionType) => option.value === "selectAll")
        ) {
          const allOptions = options.map((option) => ({
            [key]: option.value,
          }));
          field.onChange(allOptions);
        } else {
          const selectedOptions = newValue
            ? newValue.map((option: OptionType) => ({ [key]: option.value }))
            : [];
          field.onChange(selectedOptions);
        }
      };

      return (
        <Select
          {...field}
          value={options.filter((option) =>
            field.value.some((o) => o[key] === option.value)
          )}
          options={optionsWithSelectAll}
          onChange={handleOnChange}
          isMulti
          menuPlacement="auto"
        />
      );
    }
  };

  useEffect(() => {
    async function fetchUsers() {
      const { data: leadData, status: leadStatus } =
        await getUserMaster("lead_users");
      if (leadStatus) {
        const leadNames = leadData.data.map((user: LeadType) => ({
          label: user.username,
          value: user.id,
        }));
        setLeads(leadNames);
      }

      const { data: rolesData, status: rolesStatus } =
        await getUserMaster("role_master");
      if (rolesStatus) {
        const roleNames = rolesData.data.map((user: LeadType) => ({
          label: user.rolename,
          value: user.id,
        }));
        setRoles(roleNames);
      }

      const { data: docData, status: docStatus } =
        await getUserMaster("doc_types");
      if (docStatus) {
        const docTypes = docData.data.map((user: LeadType) => ({
          label: user.DocType,
          value: user.DocId,
        }));
        setDoctype(docTypes);
      }

      const { data: groupsData, status: groupsStatus } =
        await getUserMaster("group_master");
      if (groupsStatus) {
        const groupData = groupsData.data.map((user: LeadType) => ({
          label: user.GroupName,
          value: user.GroupId,
        }));
        setGroups(groupData);
      }
    }
    void fetchUsers();
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white rounded-lg p-8 max-w-6xl w-full">
        <h2 className="text-xl font-bold mb-4 flex justify-center">
          Edit User
        </h2>
        <FormProvider {...editUserForm}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((item, idx) => (
              <div className="grid grid-cols-5 gap-4" key={item.id}>
                {allFields.map((f, index) => (
                  <div className="col-span-1" key={index}>
                    {f.type === "text" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {f.label}
                          {f.name !== "leadpersonuserid" &&
                            (f.name !== "employeeid" || isEmployee) && (
                              <span className="text-red-500">*</span>
                            )}
                        </label>
                        <Controller
                          name={
                            `data.user_details.${idx}.${f.name}` as TextFields
                          }
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              className={cn(`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm 
                                ${f.name === "employeeid" && !isEmployee ? "bg-gray-200" : ""}`,
                                      editUserForm.formState.errors?.data
                                          ?.user_details &&
                                          Array.isArray(
                                              editUserForm.formState.errors.data
                                                  .user_details
                                          ) &&
                                          editUserForm.formState.errors.data.user_details
                                              .length > 0 &&
                                          editUserForm.formState.errors.data.user_details[0]?.hasOwnProperty(
                                              f.name
                                          )
                                          ? "border-red-700 border-solid border-2"
                                          : ""
                               )}
                              placeholder={f.label}
                              disabled={f.name === "employeeid" && !isEmployee} // Disable Employee ID based on isEmployee state
                            />
                          )}
                        />
                        {editUserForm.formState.errors?.data?.user_details &&
                            Array.isArray(
                                editUserForm.formState.errors.data.user_details
                            ) &&
                            editUserForm.formState.errors.data.user_details
                                .length > 0 && (
                                <span className="text-red-700 px-1 text-xs">
                                    {editUserForm.formState.errors.data.user_details[0]?.hasOwnProperty(
                                        f.name
                                    )
                                        ? editUserForm.formState.errors.data
                                            .user_details[0][f.name]?.message
                                        : ""}
                                </span>
                        )}
                      </div>
                    )}
                    {f.type === "radio" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {f.label}
                          <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name={
                            `data.user_details.${idx}.${f.name}` as RadioFields
                          }
                          control={control}
                          render={({ field }) => (
                            <div>
                              {f?.options?.map((o: any, index: any) => (
                                <div key={index} className="flex items-center">
                                  <input
                                    {...field}
                                    type="radio"
                                    className={`${f.disabled ? "bg-gray-200" : ""}`}
                                    value={o.value}
                                    checked={field.value.toString() === o.value}
                                    onChange={() => {
                                      field.onChange(o.value === "true");
                                      setIsEmployee(o.value === "true"); // Update isEmployee state based on radio selection
                                    }}
                                    disabled={f.disabled}
                                  />
                                  <span className="ml-2">{o.label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                      </div>
                    )}
                    {f.type === "select" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                          {f.label}
                        </label>
                        <Controller
                          name={
                            `data.user_details.${idx}.${f.name}` as SelectFields
                          }
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={f?.options?.find(
                                (o: any) => o.value === field.value
                              )}
                              options={f?.options}
                              onChange={(e) => {
                                field.onChange(e?.value);
                              }}
                              isMulti={false}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  Roles
                  <span className="text-red-500">*</span>
                </label>

                <Controller
                  name="data.user_roles"
                  control={control}
                  render={({ field }) => selectAllRender(field, roles, "roleid")}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  Document Type
                  <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="data.user_doctype"
                  control={control}
                  render={({ field }) => selectAllRender(field, doc_Type, "doctypeid")}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  User Groups
                  <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="data.user_groups"
                  control={control}
                  render={({ field }) => selectAllRender(field, groups, "groupid")}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                role="button"
                variant="secondary"
                radius="full"
                size="medium"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button role="button" radius="full" size="medium" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default EditModal;
